import dayjs from 'dayjs'

// The Home banner area in the app is as wide as the screen and 375 : 280 in shape, so an image of
// this size shows whole on every phone. It is the shape of the app's built-in banner (512 x 383).
export const BANNER_RECOMMENDED_SIZE = { width: 1125, height: 840 }

export const BANNER_SIZE_HINT =
	`Recommended size: ${BANNER_RECOMMENDED_SIZE.width} x ${BANNER_RECOMMENDED_SIZE.height} px (ratio 375 : 280). ` +
	'Keep text in the lower 40%: the search bar and the Story row cover the top of the banner in the app.'

export const END_TIME_QUICK_PICKS = [
	{ label: '3 hours', hours: 3 },
	{ label: '12 hours', hours: 12 },
	{ label: '1 day', hours: 24 },
	{ label: '3 days', hours: 72 },
	{ label: '7 days', hours: 168 },
]

export const DEFAULT_END_AFTER_HOURS = 24

/** End time that lies `hours` after the start time, or after now when no start time is set yet. */
export const endTimeAfter = (start: Date | null, hours: number, now: Date): Date =>
	dayjs(start ?? now).startOf('minute').add(hours, 'hour').toDate()

/** What a new banner starts with: now, to the minute, and an end one day later. */
export const defaultBannerSchedule = (now: Date): { start_at: Date; end_at: Date } => {
	const start = dayjs(now).startOf('minute').toDate()
	return { start_at: start, end_at: endTimeAfter(start, DEFAULT_END_AFTER_HOURS, now) }
}

// UD-562: the page manages two kinds of banner. The Main Banner is the image behind the header of
// the app's Home screen. The Banner Promotion is a square picture the app shows once as a popup,
// with an optional link. One of each kind can be active at the same time.
export type BannerKind = 'main' | 'promotion'

export const BANNER_KIND_OPTIONS: { value: BannerKind; label: string }[] = [
	{ value: 'main', label: 'Main Banner' },
	{ value: 'promotion', label: 'Banner Promotion' },
]

export const PROMOTION_RECOMMENDED_SIZE = { width: 1080, height: 1080 }

export const PROMOTION_SIZE_HINT =
	`Square picture, ${PROMOTION_RECOMMENDED_SIZE.width} x ${PROMOTION_RECOMMENDED_SIZE.height}`

export const PROMOTION_LINK_LABEL = 'Link (optional)'

export const PROMOTION_LINK_HELP = 'Opens when a user taps the picture. Leave empty to only show the picture.'

export const BANNER_LINK_MAX_LENGTH = 2000

// How many times the app shows a promotion to each account. The server counts per account, so
// signing out, switching account or reinstalling the app does not start the count again.
export const PROMOTION_SHOWS_LABEL = 'Times shown to each user'

// The list column is narrow, so its header is the short form of the label.
export const PROMOTION_SHOWS_COLUMN = 'Times shown'

// The choices of the field. The first one is what a new promotion starts with and what a row
// saved without a number counts as.
export const PROMOTION_SHOWS_OPTIONS = [1, 2, 3]

export const PROMOTION_SHOWS_MIN = PROMOTION_SHOWS_OPTIONS[0]

type BannerKindText = {
	createTitle: string
	editTitle: string
	createButton: string
	updateButton: string
	/** How the kind is named in the success and delete messages. */
	name: string
	deleteQuestion: string
	sizeHint: string
	onlyOneNote: string
	overlapError: string
}

// The Main Banner keeps every text it had before; only its titles name the kind.
export const BANNER_KIND_TEXT: Record<BannerKind, BannerKindText> = {
	main: {
		createTitle: 'Create Main Banner',
		editTitle: 'Edit Main Banner',
		createButton: 'Create banner',
		updateButton: 'Update banner',
		name: 'Banner',
		deleteQuestion: 'Do you want to delete this banner?',
		sizeHint: BANNER_SIZE_HINT,
		onlyOneNote: 'Only one Main Banner can be active at a time. All times use Asia/Ho_Chi_Minh.',
		overlapError: 'Banner schedule overlaps an existing banner',
	},
	promotion: {
		createTitle: 'Create Banner Promotion',
		editTitle: 'Edit Banner Promotion',
		createButton: 'Create Banner Promotion',
		updateButton: 'Update Banner Promotion',
		name: 'Banner Promotion',
		deleteQuestion: 'Do you want to delete this Banner Promotion?',
		sizeHint: PROMOTION_SIZE_HINT,
		onlyOneNote:
			'Several Banner Promotions can run at the same time; each account sees at most one a day, in the order of this list. They can run together with a Main Banner. All times use Asia/Ho_Chi_Minh.',
		overlapError: 'Banner Promotion schedule overlaps an existing Banner Promotion',
	},
}

/** The kind of a banner row. A row without a type is a Main Banner (every banner made before UD-562). */
export const bannerKindOf = (banner: { type?: string | null } | null | undefined): BannerKind =>
	banner?.type === 'promotion' ? 'promotion' : 'main'

/** What is wrong with a promotion link, or null. An empty link is fine: the link is optional. */
export const bannerLinkError = (value: string | null | undefined): string | null => {
	const link = (value ?? '').trim()
	if (!link) return null
	let isHttp = false
	try {
		isHttp = ['http:', 'https:'].includes(new URL(link).protocol)
	} catch (_) {
		isHttp = false
	}
	if (!isHttp) return 'Link must start with http:// or https://'
	if (link.length > BANNER_LINK_MAX_LENGTH) return `Link must be at most ${BANNER_LINK_MAX_LENGTH} characters`
	return null
}

/** The number a promotion row was saved with, as the form holds it: 1 when none was set. */
export const promotionShowsOf = (banner: { shows_per_user?: number | null } | null | undefined): number =>
	typeof banner?.shows_per_user === 'number' ? banner.shows_per_user : PROMOTION_SHOWS_MIN

/**
 * The choices of the select. A number saved outside the list (possible only through the API) is
 * added, so the form shows it and saving keeps it.
 */
export const promotionShowsChoices = (current: number): { value: string; label: string }[] =>
	(PROMOTION_SHOWS_OPTIONS.includes(current) ? PROMOTION_SHOWS_OPTIONS : [...PROMOTION_SHOWS_OPTIONS, current].sort((a, b) => a - b))
		.map((count) => ({ value: String(count), label: String(count) }))

type ScheduledBanner = { id?: string; type?: string | null; start_at: string | Date; end_at: string | Date }

/**
 * Whether the schedule collides with another banner OF THE SAME KIND. A Main Banner and a Banner
 * Promotion may run at the same time; an end time equal to another start time is not a collision.
 */
export const overlapsSameKind = (
	banners: ScheduledBanner[],
	kind: BannerKind,
	start: Date | null,
	end: Date | null,
	exceptId?: string,
): boolean =>
	banners.some(
		(banner) =>
			banner.id !== exceptId &&
			bannerKindOf(banner) === kind &&
			dayjs(start).isBefore(dayjs(banner.end_at)) &&
			dayjs(end).isAfter(dayjs(banner.start_at)),
	)
